import { useQueryClient } from "@tanstack/react-query";
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Deployment } from "../../api/types";
import { flashAttention } from "../../components/attention";
import { ESCAPE, registerEscape, topEscapePriority } from "../../components/escapeStack";
import { k, t, tc, tServer } from "../../i18n/i18n";
import { type Notice, useUi } from "../../state/ui";
import { closestPoint, moveM } from "../geo";
import { history, isConflict } from "../history";
import { useLineLabel } from "../lineNames";
import { orderActions, withOrders } from "../orderSync";
import { historyKeyAction, isEditableTarget } from "../useHistoryKeys";
import { extendZoneAction, handleZoneRefusal } from "../zones/zoneAttention";
import {
  clampToBounds,
  type Bounds,
  insertBend,
  insertPair,
  type LineName,
  mergeFolds,
  moveBend,
  moveLimaEnd,
  moveLinked,
  moveSingle,
  removeBend,
  removePair,
  type RuleCode,
  RULES,
  type SingleMove,
  translateLima,
  type UserCorridor,
  validate,
  widthAlerts,
  type WidthContext,
  widthContextOf,
  widths,
} from "./corridorEdit";
import { type CorridorFrame, frameOf, helperDistance, type LonLat } from "./corridorFrame";
import { forLevel, formatKm, formatKmBound } from "./corridorWidths";
import { type GestureTag, useGestureState } from "./gestureState";
import {
  allHandles,
  buildHandles,
  focusOf,
  type HandleHit,
  majorVertices,
  metersPerPixel,
  PICK_RADIUS_COARSE_PX,
  PICK_RADIUS_PX,
  pickHandle,
  sameHandle,
} from "./handles";
import { currentPreviews, measureEndPoints, measureView } from "./measureView";
import { canAnimate } from "./motion";
import { type ManoeuvreHandle, useManoeuvre } from "./useManoeuvre";
import { useManoeuvreWrites } from "./useManoeuvreWrites";
import { refuseIfDossierLocked } from "../../sharing/access";

/** Gestes du mode édition du fuseau (docs/14 §2.3, §2.4) : glissement d'un sommet (paire liée par défaut ; ⌥ ou
 *  « Garder la largeur » décochée : sommet seul, largeur en direct, aimantation à 3 % ; ⇧ : décalage latéral
 *  pur), poignée de milieu qui insère une paire, double-clic qui retire une paire (deux au moins), clic qui
 *  sélectionne ; LIMA : glissement entier le long du fuseau (repère K2), extrémité qui glisse le long de sa
 *  limite, courbures. Un sommet glissé entraîne son voisinage en douceur (`corridorEdit`, « Déformation douce »),
 *  jusqu'aux poignées voisines affichées. Aperçu à chaque image (`useManoeuvre.working` + `validate`), aucune
 *  requête pendant le geste ; au relâcher, état invalide : avis qui nomme la règle et retour animé de 200 ms ;
 *  valide : `PUT /corridor`, l'aperçu reste jusqu'à la réponse (B28). Clavier : flèches (100 m, ⇧ 1 km, cumulées
 *  depuis le début du groupe, une entrée d'historique 400 ms après la dernière touche),
 *  Tab / ⇧Tab sur les sommets (annonce `aria-live`), ⌫ / Suppr, Échap par la pile partagée.
 *
 *  La machine à états du pointeur (seuils, appui long, relâcher hors de la carte) reste dans MapCanvas, qui
 *  appelle `grab` à l'activation du glissement, `drag` à chaque mouvement et `release` au relâcher. */

/** Seuil d'enregistrement d'un geste (en deçà, l'iPad le traite comme un simple toucher). */
export const MIN_MOVE_M = 10;
/** Retour animé d'une géométrie refusée. */
export const RETURN_MS = 200;
/** Flèches : pas de 100 m, 1 km avec ⇧ ; une seule entrée d'historique 400 ms après la dernière touche. */
export const ARROW_STEP_M = 100;
export const ARROW_STEP_SHIFT_M = 1000;
export const ARROW_GROUP_MS = 400;

export type ArrowKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight";
const ARROWS: readonly string[] = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

export type HistoryStep = "undo" | "redo";

/** Garde des annulations de la carte, posée par le hook des gestes : un ⌘Z (ou ↶) qui tomberait pendant un
 *  glissement, un groupe de flèches pas encore enregistré ou une retouche en vol ne doit ni être écrasé par elle,
 *  ni défaire l'action d'avant. */
let historyGuard: ((step: HistoryStep) => boolean) | null = null;

/** Même garde pour les lignes de la ZO (`useMeasureGestures`, docs/15 §4.3) : glissement en cours, écriture en vol. */
let measureHistoryGuard: ((step: HistoryStep) => boolean) | null = null;

/** Pose (ou retire, avec null) la garde des glissements de lignes de la ZO ; rend son retrait. */
export function setMeasureHistoryGuard(guard: ((step: HistoryStep) => boolean) | null): () => void {
  measureHistoryGuard = guard;
  return () => {
    if (measureHistoryGuard === guard) measureHistoryGuard = null;
  };
}

/** Vrai si le mode édition a pris l'annulation à sa charge (geste abandonné, flèches en attente défaites, ou
 *  annulation rejouée à la réponse de la retouche en vol). */
export function interceptHistoryStep(step: HistoryStep): boolean {
  return (measureHistoryGuard?.(step) ?? false) || (historyGuard?.(step) ?? false);
}

/** Annuler / Rétablir depuis les boutons ↶ ↷ de la carte, en passant par la garde du mode édition. */
export function runHistoryStep(step: HistoryStep): void {
  if (interceptHistoryStep(step)) return;
  if (step === "undo") history.undo();
  else history.redo();
}

/** Cap (degrés) et distance (m) d'un appui de flèche, la carte étant tournée de `bearing` : ↑ vers le haut de
 *  l'écran. */
export function arrowStep(key: ArrowKey, shift: boolean, bearing = 0): { heading: number; distance: number } {
  const turn = { ArrowUp: 0, ArrowRight: 90, ArrowDown: 180, ArrowLeft: 270 }[key];
  return { heading: (((bearing + turn) % 360) + 360) % 360, distance: shift ? ARROW_STEP_SHIFT_M : ARROW_STEP_M };
}

/** Sommets dans l'ordre de Tab : de l'arrière vers l'avant, limite gauche puis droite. */
export function vertexOrder(pairs: number): Extract<ManoeuvreHandle, { kind: "vertex" }>[] {
  const out: Extract<ManoeuvreHandle, { kind: "vertex" }>[] = [];
  for (const side of ["left", "right"] as const)
    for (let index = 0; index < pairs; index++) out.push({ kind: "vertex", side, index });
  return out;
}

/** Sommet suivant (ou précédent) dans l'ordre de Tab ; sans sélection, le premier (ou le dernier). */
export function cycleVertex(
  current: ManoeuvreHandle | null,
  pairs: number,
  backwards: boolean,
): Extract<ManoeuvreHandle, { kind: "vertex" }> | null {
  const order = vertexOrder(pairs);
  if (!order.length) return null;
  const at = current?.kind === "vertex" ? order.findIndex((h) => sameHandle(h, current)) : -1;
  if (at < 0) return backwards ? order[order.length - 1]! : order[0]!;
  return order[(at + (backwards ? -1 : 1) + order.length) % order.length]!;
}

/** Annonce du parcours au clavier : « Sommet 4 sur 12, limite gauche, largeur 18,15 km ». */
export function vertexAnnouncement(handle: Extract<ManoeuvreHandle, { kind: "vertex" }>, pairs: number, width: number): string {
  const position = vertexOrder(pairs).findIndex((h) => sameHandle(h, handle)) + 1;
  return t("Sommet {n} sur {total}, {side}, largeur {width}", {
    n: position,
    total: pairs * 2,
    side: handle.side === "left" ? t("limite gauche") : t("limite droite"),
    width: formatKm(width),
  });
}

/** Le geste a-t-il déplacé quelque chose de plus de 10 m (sommets, extrémités et courbures de LIMA) ? Un nombre
 *  de paires ou de LIMA changé compte comme un déplacement. */
export function movedEnough(a: UserCorridor, b: UserCorridor, metres = MIN_MOVE_M): boolean {
  if (a.left.length !== b.left.length || a.right.length !== b.right.length) return true;
  for (const side of ["left", "right"] as const) {
    const ra = a[side];
    const rb = b[side];
    for (let i = 0; i < ra.length; i++) if (helperDistance(ra[i]!, rb[i]!) >= metres) return true;
  }
  const names = new Set([...Object.keys(a.lines), ...Object.keys(b.lines)]) as Set<LineName>;
  if (!names.size) return false;
  const frame = frameOf(b);
  for (const name of names) {
    const la = a.lines[name];
    const lb = b.lines[name];
    if (!la || !lb || la.bends.length !== lb.bends.length || la.hours !== lb.hours) return true;
    const ends = [-1, 1] as const;
    for (const k of [0, 1] as const)
      if (helperDistance(frame.point(la.ends[k], ends[k]), frame.point(lb.ends[k], ends[k])) >= metres) return true;
    for (let i = 0; i < la.bends.length; i++)
      if (helperDistance(frame.point(...la.bends[i]!), frame.point(...lb.bends[i]!)) >= metres) return true;
  }
  return false;
}

/** Géométrie intermédiaire du retour animé : sommets et LIMA interpolés si les structures sont égales, sinon
 *  l'arrivée. */
export function interpolateModel(a: UserCorridor, b: UserCorridor, t: number): UserCorridor {
  if (t >= 1 || a.left.length !== b.left.length || a.right.length !== b.right.length) return b;
  const mix = (p: LonLat, q: LonLat): LonLat => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
  const lines: UserCorridor["lines"] = {};
  for (const [name, lb] of Object.entries(b.lines) as [LineName, NonNullable<UserCorridor["lines"][LineName]>][]) {
    const la = a.lines[name];
    lines[name] =
      la && la.bends.length === lb.bends.length
        ? {
            ...lb,
            ends: [la.ends[0] + (lb.ends[0] - la.ends[0]) * t, la.ends[1] + (lb.ends[1] - la.ends[1]) * t],
            bends: lb.bends.map(([s, u], i) => [la.bends[i]![0] + (s - la.bends[i]![0]) * t, la.bends[i]![1] + (u - la.bends[i]![1]) * t]),
          }
        : lb;
  }
  return {
    ...b,
    left: b.left.map((p, i) => mix(a.left[i]!, p)),
    right: b.right.map((p, i) => mix(a.right[i]!, p)),
    lines,
  };
}

/** Libellé court de la règle violée, pour la puce du geste (« Les limites se croisent »). */
const RULE_TAGS: Partial<Record<RuleCode, string>> = {
  crossing: k("Les limites se croisent"),
  turn: k("Virage de plus de 90°"),
  orientation: k("Limites inversées par endroits"),
  degenerate: k("Deux paires de sommets confondues"),
  polygon: k("Contour du fuseau invalide"),
  width: k("Largeur hors des bornes admises"),
  axis_length: k("Axe de 5 à 200 km"),
  line_order: k("Ordre des LIMA non respecté"),
  line_crossing: k("Deux LIMA se croisent"),
  line_inside: k("LIMA hors du fuseau"),
  hours_order: k("GDH des LIMA non croissants"),
  pairs_count: k("De 2 à 64 paires de sommets"),
};

/** `free` : fuseau d'un terrain libre — l'emprise se dit « Hors du terrain » (sur un camp, « Hors du camp »), comme
 *  l'avis du même geste. */
export function ruleTag(rule: RuleCode, free = false, level = ""): string {
  if (rule === "bounds" || rule === "world") return free ? t("Hors du terrain") : t("Hors du camp");
  // Corps d'armée (docs/08 §23) : axe de 100 à 800 km.
  if (rule === "axis_length" && level === "CORPS") return t("Axe de 100 à 800 km");
  const short = RULE_TAGS[rule];
  return short ? t(short) : tServer(RULES[rule]);
}

/** Action attendue après un refus, selon la règle violée (avis du relâcher ou du groupe de flèches). */
const RULE_HINTS: Partial<Record<RuleCode, string>> = {
  crossing: k("Déplacez le sommet moins loin, ou ajoutez un sommet par la poignée de milieu pour assouplir la limite."),
  turn: k("Adoucissez le virage : déplacez le sommet moins loin, ou ajoutez un sommet par la poignée de milieu."),
  orientation: k("Gardez la limite gauche à gauche de la limite droite."),
  polygon: k("Déplacez le sommet moins loin, ou ajoutez un sommet par la poignée de milieu pour assouplir la limite."),
  degenerate: k("Écartez les deux paires de sommets, ou retirez l'une d'elles."),
  axis_length: k("L'axe du fuseau mesure de 5 à 200 km."),
  bounds: k("Restez dans l'emprise affichée en tirets gris."),
  world: k("Restez dans l'emprise affichée en tirets gris."),
  line_order: k("Gardez les LIMA dans l'ordre L0, L1, L2, L3, à 50 m au moins l'une de l'autre."),
  line_crossing: k("Écartez les deux LIMA pour qu'elles ne se croisent plus."),
  line_inside: k("Gardez la LIMA entre les deux limites du fuseau."),
  hours_order: k("Saisissez des GDH croissants de L0 à L3."),
};

/** Action attendue pour la règle de largeur : les bornes dures chiffrées de la clé de gabarit (docs/15 §2.4 :
 *  GTIA d'infanterie 1,5 à 54,45 km ; union des familles du niveau sans clé) et le sens du geste. */
function widthHint(level: string, levelKey: string | null): string {
  const doctrine = forLevel(level, levelKey);
  if (!doctrine) return t("Écartez des limites trop proches, rapprochez des limites trop écartées.");
  return t("Ramenez la largeur entre {min} et {max} km : écartez des limites trop proches, rapprochez des limites trop écartées.", {
    min: formatKmBound(doctrine.hard_min_m),
    max: formatKmBound(doctrine.hard_max_m),
  });
}

/** Avis d'une modification refusée sur place (relâcher, groupe de flèches) : le message de la règle, comme le
 *  serveur le donnerait, et l'action attendue ; le fuseau revient à sa forme précédente (retour animé).
 *  `level`, `levelKey` : niveau du fuseau et clé de gabarit (bornes chiffrées de la règle de largeur). */
export function refusalNotice(rule: RuleCode, level = "", levelKey: string | null = null): Omit<Notice, "id"> {
  const corpsAxis = rule === "axis_length" && level === "CORPS";
  const hint =
    rule === "width"
      ? widthHint(level, levelKey)
      : corpsAxis
        ? t("Au niveau corps d'armée, l'axe du fuseau mesure de 100 à 800 km.")
        : RULE_HINTS[rule]
          ? t(RULE_HINTS[rule]!)
          : null;
  return {
    text: tServer(RULES[rule]),
    detail: hint ? t("Le fuseau reprend sa forme précédente. {hint}", { hint }) : t("Le fuseau reprend sa forme précédente."),
    tone: "warn",
  };
}

/** Puce de largeur d'un sommet seul (⌥) : largeur de la paire saisie, défaut et plage de la clé de gabarit ;
 *  ton d'alerte selon la règle du serveur (paire retouchée que le geste éloigne de la plage doctrinale, docs/15
 *  §2.4), jamais pour le seul calage de l'amorce ni une fois aimantée. `context` : `widthContextOf`. */
export function widthGestureTag(
  moved: SingleMove,
  level: string,
  context: WidthContext,
  index: number,
  at: LonLat,
): Extract<GestureTag, { kind: "width" }> {
  return {
    kind: "width",
    at,
    width: moved.width,
    doctrinal: moved.doctrinal,
    range: moved.range,
    alert: !moved.snapped && Boolean(widthAlerts(moved.corridor, level, context)?.alerts.includes(index)),
    snapped: moved.snapped,
  };
}

/** Curseur d'une poignée (docs/14 §2.3) : `grab` (`ew-resize` avec ⌥ sur un sommet), `copy` sur une poignée de
 *  milieu, `grabbing` pendant le geste, `not-allowed` tant que le dossier n'est pas enregistré. */
export function handleCursor(
  handle: ManoeuvreHandle | null,
  options: { active?: boolean; alt?: boolean; canEdit?: boolean },
): string | null {
  if (options.active) return "grabbing";
  if (!handle) return null;
  if (options.canEdit === false) return "not-allowed";
  if (handle.kind === "mid" || handle.kind === "limaMid") return "copy";
  if (handle.kind === "vertex" && options.alt) return "ew-resize";
  return "grab";
}

export interface Modifiers {
  alt: boolean;
  shift: boolean;
}

type SessionKind = "vertex" | "limaEnd" | "limaBend" | "limaWhole";

export interface GestureSession {
  kind: SessionKind;
  /** Poignée saisie (après insertion pour une poignée de milieu) ; null pour la LIMA entière. */
  handle: ManoeuvreHandle | null;
  line: LineName | null;
  /** Géométrie avant le geste (retour en cas de refus). */
  origin: UserCorridor;
  /** Géométrie au début du glissement (après insertion d'une paire ou d'une courbure). */
  start: UserCorridor;
  frame: CorridorFrame;
  grab: LonLat;
  /** Écart de saisie : position de la poignée moins le pointeur (pas de saut au premier mouvement, B28). */
  offset: [number, number];
  last: UserCorridor;
  lastPointer: LonLat;
  blocked: LineName | null;
  outside: boolean;
  /** Sommets affichés de la limite saisie au début du geste (déformation douce jusqu'à eux). */
  anchors: ReadonlySet<number> | null;
  /** Sélection à rendre si la géométrie revient à `origin` alors que `start` a changé le nombre de paires (plis
   *  de l'amorce effacés, paire insérée) : les rangs de `start` ne valent plus. */
  originSelection: ManoeuvreHandle | null;
  /** Géométrie d'où se mesure le seuil de 10 m : `start` quand seuls des plis ont été effacés (rien à enregistrer
   *  sans mouvement), sinon `origin` (une paire ou une courbure insérée compte comme un déplacement). */
  reference: UserCorridor;
  /** Paire liée quels que soient ⌥ et « Garder la largeur » : LIMAR ou LIMAV saisie en édition, la paire
   *  d'extrémité du fuseau glisse largeur gardée (docs/15 §4.3). */
  linked: boolean;
}

interface Options {
  mapRef: RefObject<{ getMap: () => unknown } | null>;
  frameRef: RefObject<HTMLElement | null>;
  deployment: Deployment | undefined;
  /** Glissement d'une LIMA entière (`useUnitMoves().moveLine`) : fuseau personnalisé, dans ou hors du mode édition. */
  moveLine: {
    mutate: (
      input: { line: LineName; lon: number; lat: number },
      options?: { onSettled?: () => void; onError?: () => void },
    ) => void;
  };
}

/** Sous-ensemble de la carte utilisé ici (commun à Mapbox GL JS et MapLibre GL JS). */
interface MapLike {
  project: (p: LonLat) => { x: number; y: number };
  getZoom: () => number;
  getBearing: () => number;
  getCenter: () => { lat: number };
  getContainer: () => HTMLElement;
  easeTo: (options: { center: LonLat; duration: number }) => void;
}

const coarsePointer = () => {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
};

export function useManoeuvreGestures({ mapRef, frameRef, deployment, moveLine }: Options) {
  const editing = useManoeuvre((s) => s.editing);
  const selected = useManoeuvre((s) => s.selected);
  const selectedLine = useManoeuvre((s) => s.selectedLine);
  const hover = useManoeuvre((s) => s.hover);
  const active = useManoeuvre((s) => s.active);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const exerciseId = useUi((s) => s.exerciseId);
  const lineLabel = useLineLabel();
  const writes = useManoeuvreWrites();
  const { setCorridor, canEdit } = writes;
  const [alt, setAlt] = useState(false);

  const corridor = deployment?.corridor ?? null;
  const model = corridor?.model ?? null;
  const bounds = (corridor?.bounds ?? null) as Bounds | null;
  const level = model?.level ?? "";
  // Largeurs du mémento (docs/15 §2.4) : clé de gabarit (bornes dures, aimantation, alerte) et référence des
  // paires (largeurs de l'amorce ; fuseau antérieur : toutes les paires comparées à la plage).
  const levelKey = deployment?.level_key ?? null;
  const widthContext = useMemo<WidthContext>(() => widthContextOf(corridor, levelKey), [levelKey, corridor]);
  const zone = corridor?.origin === "free" || corridor?.origin === "zone";
  // Modèle = amorce non retouchée (fuseau automatique ou zone libre) : ses plis s'effacent au premier geste.
  const seed = corridor !== null && !corridor.custom;
  const enabled = editing && model !== null && !deployment?.centac;

  // Lectures fraîches dans les écouteurs et les rappels asynchrones.
  const queryClient = useQueryClient();
  const live = useRef({ model, bounds, level, levelKey, widthContext, canEdit, zone, seed, lineLabel, setCorridor, notify, deployment, queryClient });
  live.current = { model, bounds, level, levelKey, widthContext, canEdit, zone, seed, lineLabel, setCorridor, notify, deployment, queryClient };
  const sessionRef = useRef<GestureSession | null>(null);
  const returnFrame = useRef(0);
  /** Arrivée du retour animé en cours : rétablie d'un coup si le retour est interrompu (nouveau geste, flèche),
   *  pour que rien ne reparte d'une image intermédiaire. */
  const returnTarget = useRef<UserCorridor | null>(null);
  /** Groupe de flèches en attente : géométrie d'avant le groupe (`origin`, retour en cas de refus), géométrie de
   *  départ des mouvements (`start` : plis de l'amorce effacés), sommet déplacé (rang dans `start`, et dans
   *  `origin`), position voulue cumulée (chaque appui repart de `start`, comme un glissement : la déformation
   *  douce ne s'empile pas). */
  const keyGroup = useRef<{
    origin: UserCorridor;
    start: UserCorridor;
    timer: number;
    side: "left" | "right";
    index: number;
    originIndex: number;
    target: LonLat;
    anchors: ReadonlySet<number> | null;
  } | null>(null);
  /** Retouche envoyée, en attente de réponse : vrai si elle est enregistrée. */
  const inflight = useRef<Promise<boolean> | null>(null);

  const map = useCallback(() => (mapRef.current?.getMap() as MapLike | undefined) ?? null, [mapRef]);

  /** Géométrie du serveur telle qu'elle est montrée : la cible d'annulations en attente d'envoi (aperçu de
   *  l'historique, dessiné tout de suite), sinon le modèle de la dernière réponse. */
  const shownServer = useCallback(
    (): UserCorridor | null => useGestureState.getState().historyPreview ?? live.current.model,
    [],
  );

  /** Géométrie de départ d'un geste : l'aperçu affiché (retouche en attente de réponse, annulations en attente)
   *  ou le modèle du serveur — celle des poignées dessinées. */
  const base = useCallback((): UserCorridor | null => useManoeuvre.getState().working ?? shownServer(), [shownServer]);

  /** Géométrie à rétablir (null, ou celle du serveur : plus d'aperçu de geste). */
  const restore = useCallback((target: UserCorridor | null) => {
    const server = target === live.current.model || target === useGestureState.getState().historyPreview;
    useManoeuvre.getState().setWorking(target && !server ? target : null);
  }, []);

  /** Retour animé interrompu : son arrivée est rétablie d'un coup (aucun geste ne repart d'une image du retour). */
  const cancelReturn = useCallback(() => {
    if (returnFrame.current) {
      cancelAnimationFrame(returnFrame.current);
      returnFrame.current = 0;
      restore(returnTarget.current);
    }
    returnTarget.current = null;
  }, [restore]);

  /** Retour animé (200 ms) d'une géométrie refusée vers `to`. `via` : même géométrie que `to` au nombre de paires
   *  de `from` (plis effacés, paire insérée au début du geste) : le retour s'anime jusqu'à elle, puis `to` est
   *  rétablie (sans `via`, un nombre de paires différent rétablit `to` d'un coup). */
  const animateBack = useCallback(
    (from: UserCorridor, to: UserCorridor | null, via?: UserCorridor | null) => {
      cancelReturn();
      const target = to ?? shownServer();
      const path = via && target && via.left.length === from.left.length ? via : target;
      if (!target || !path || !canAnimate() || from.left.length !== path.left.length) {
        restore(target);
        return;
      }
      returnTarget.current = target;
      const started = performance.now();
      const step = (time: number) => {
        const raw = Math.min(1, Math.max(0, (time - started) / RETURN_MS));
        const eased = raw * raw * (3 - 2 * raw);
        if (raw >= 1) {
          returnFrame.current = 0;
          returnTarget.current = null;
          restore(target);
          return;
        }
        useManoeuvre.getState().setWorking(interpolateModel(from, path, eased));
        returnFrame.current = requestAnimationFrame(step);
      };
      returnFrame.current = requestAnimationFrame(step);
    },
    [cancelReturn, restore, shownServer],
  );

  /** Enregistre une géométrie valide (`PUT /corridor`) : l'aperçu reste affiché jusqu'à la réponse, puis la
   *  réponse prend le relais (unités et symboles glissent, transition de données). Refus : retour animé et message
   *  du serveur ; conflit (412 / 409) : historique remis à zéro et dossier rechargé par la couche d'écriture.
   *  Faux si la géométrie est refusée sur place (avis et retour animé, via `via` : voir `animateBack`). */
  const commit = useCallback(
    (next: UserCorridor, origin: UserCorridor | null, label?: string, via?: UserCorridor | null): boolean => {
      const { bounds: box, level: lvl, levelKey: key, widthContext: context } = live.current;
      const check = validate(next, box, lvl, context);
      if (check.rules.length) {
        live.current.notify(refusalNotice(check.rules[0]!, lvl, key));
        animateBack(next, origin, via);
        return false;
      }
      useManoeuvre.getState().setWorking(next);
      const run = (async (): Promise<boolean> => {
        try {
          const write = await live.current.setCorridor.mutateAsync({ corridor: next, notify: false, label });
          notifyNewWarnings(write.result, write.previous, live.current.notify);
        } catch (error) {
          const shown = useManoeuvre.getState().working === next;
          // Conflit (412 / 409) : avis, historique remis à zéro et rechargement par la couche d'écriture.
          if (isConflict(error)) {
            if (shown) useManoeuvre.getState().setWorking(null);
            return false;
          }
          // Refus propre à la zone libre (fuseau hors de l'empreinte préparée : « Étendre la zone » désigné ; terrain
          // pas prêt) : avis et action attendue, puis retour animé.
          if (handleZoneRefusal(live.current.queryClient, error, t("Fuseau non enregistré"))) {
            if (shown) animateBack(next, null);
            return false;
          }
          // Refus signalé même si un autre geste a commencé depuis ; le retour animé, seulement si cette retouche
          // est encore celle qui est montrée.
          live.current.notify({
            text: t("Fuseau non enregistré"),
            detail: error instanceof Error && error.message ? error.message : t("Serveur injoignable"),
            tone: "warn",
          });
          if (shown) animateBack(next, null);
          return false;
        }
        if (useManoeuvre.getState().working === next) useManoeuvre.getState().setWorking(null);
        return true;
      })();
      inflight.current = run;
      void run.finally(() => {
        if (inflight.current === run) inflight.current = null;
      });
      return true;
    },
    [animateBack],
  );

  /** Sélection rendue au sommet d'avant le groupe ou le geste quand la géométrie revient à une autre numérotation
   *  des paires (plis effacés au début, puis refus ou annulation). */
  const reselect = useCallback((handle: ManoeuvreHandle | null, shifted: boolean) => {
    if (shifted) useManoeuvre.getState().select(handle);
  }, []);

  /** Groupe de flèches en attente : enregistré tout de suite (sortie du mode, autre geste). */
  const flushKeys = useCallback(() => {
    const group = keyGroup.current;
    if (!group) return;
    window.clearTimeout(group.timer);
    keyGroup.current = null;
    useGestureState.getState().setFeedback(null);
    const next = useManoeuvre.getState().working;
    const shifted = group.start.left.length !== group.origin.left.length;
    const back = { kind: "vertex" as const, side: group.side, index: group.originIndex };
    if (next && movedEnough(group.start, next)) {
      if (!commit(next, group.origin, undefined, group.start)) reselect(back, shifted);
    } else {
      restore(group.origin);
      reselect(back, shifted);
    }
  }, [commit, reselect, restore]);

  // --- Poignées sous le pointeur ------------------------------------------------------------------------------

  const handlesNow = useCallback(() => {
    const m = map();
    const geometry = base();
    if (!m || !geometry || geometry.left.length < 2) return [];
    const state = useManoeuvre.getState();
    const mpp = metersPerPixel(m.getZoom(), m.getCenter().lat);
    // Extrémités des lignes de la ZO du temps affiché, telles qu'elles sont dessinées (aperçu compris).
    const measures = measureView(live.current.deployment, currentPreviews());
    return allHandles(
      buildHandles(geometry, {
        mpp,
        selectedLine: state.selectedLine,
        emphasised: [state.hover, state.active, state.selected],
        lines: useUi.getState().controlVisible.lima !== false,
        measureEnds: measures ? measureEndPoints(measures.measures.lines, useUi.getState().phase) : [],
      }),
    );
  }, [base, map]);

  /** Sommets affichés en permanence d'une limite au zoom courant (déformation douce jusqu'à eux). */
  const anchorsOf = useCallback(
    (geometry: UserCorridor, side: "left" | "right"): ReadonlySet<number> | null => {
      const m = map();
      if (!m) return null;
      return majorVertices(geometry[side], metersPerPixel(m.getZoom(), m.getCenter().lat));
    },
    [map],
  );

  /** Poignée saisie sous le point (pixels de la carte). */
  const pick = useCallback(
    (point: { x: number; y: number }, touch = false): HandleHit | null => {
      if (!enabled) return null;
      const m = map();
      if (!m) return null;
      const radius = touch || coarsePointer() ? PICK_RADIUS_COARSE_PX : PICK_RADIUS_PX;
      // Poignées confondues : celles de l'objet sélectionné (LIMA, ligne de la ZO dont la fiche est ouverte)
      // l'emportent, dessinées au-dessus ; sinon la plus proche du pointeur.
      const focus = focusOf(useManoeuvre.getState().selectedLine, useUi.getState().selection);
      return pickHandle(handlesNow(), point, (p) => m.project(p), radius, focus);
    },
    [enabled, map, handlesNow],
  );

  /** Survol : poignée survolée (la bulle des unités est suspendue tant qu'il y en a une) et pointeur. */
  const hoverAt = useCallback(
    (point: { x: number; y: number }, lngLat: LonLat | null): HandleHit | null => {
      const state = useManoeuvre.getState();
      if (!enabled) {
        if (state.hover) state.setHover(null);
        return null;
      }
      useGestureState.getState().setPointer(lngLat);
      if (sessionRef.current) return null;
      const hit = lngLat ? pick(point) : null;
      if (!sameHandle(hit?.handle ?? null, state.hover)) state.setHover(hit?.handle ?? null);
      return hit;
    },
    [enabled, pick],
  );

  /** Toucher en mode édition : les sommets mineurs voisins apparaissent autour du doigt (au doigt, pas de
   *  survol) et restent affichés autour du dernier toucher. */
  const touchAt = useCallback(
    (lngLat: LonLat) => {
      if (enabled) useGestureState.getState().setPointer(lngLat);
    },
    [enabled],
  );

  // Pointeur sorti de la carte : la poignée n'est plus survolée ; sa dernière position est gardée, pour que les
  // sommets mineurs voisins s'effacent avec la couche plutôt que d'un coup.
  const leave = useCallback(() => {
    if (!sessionRef.current && useManoeuvre.getState().hover) useManoeuvre.getState().setHover(null);
  }, []);

  // --- Glissements --------------------------------------------------------------------------------------------

  /** Refus : dossier non enregistré (le bouton « Enregistrer » est désigné, retour à la vue ensuite). */
  const refuseNotSaved = useCallback(() => {
    if (refuseIfDossierLocked()) return; // lecture seule (docs/17 §3.2) : le bandeau, pas l'enregistrement
    requestSave();
    notify({
      text: exerciseId
        ? t("Enregistrez les modifications du dossier pour {action}", { action: t("modifier le fuseau") })
        : t("Créez le dossier pour {action}", { action: t("modifier le fuseau") }),
      detail: t("Le fuseau retouché est mémorisé dans le dossier d'exercice."),
      tone: "info",
    });
  }, [exerciseId, notify, requestSave]);

  /** Début d'un glissement de poignée (au-delà du seuil d'activation) : `grab` = point saisi au départ ;
   *  `options.linked` : paire liée quels que soient ⌥ et « Garder la largeur » (LIMAR, LIMAV). */
  const grab = useCallback(
    (handle: ManoeuvreHandle, grabAt: LonLat, at: LonLat, options: { linked?: boolean } = {}): GestureSession | null => {
      cancelReturn();
      flushKeys();
      const origin = base();
      if (!origin || origin.left.length < 2) return null;
      const before = useManoeuvre.getState().selected;
      let start = origin;
      let held: ManoeuvreHandle = handle;
      let position = at;
      let originSelection: ManoeuvreHandle | null = before;
      if (handle.kind === "vertex") {
        // Plis de l'amorce effacés avant le glissement (`mergeFolds`), la paire saisie gardée.
        const merged = mergeFolds(origin, { keep: handle.index, seed: live.current.seed });
        start = merged.corridor;
        held = { ...handle, index: merged.index(handle.index) };
        if (start !== origin) position = start[held.side][held.index]!;
        originSelection = handle;
      } else if (handle.kind === "mid") {
        const merged = mergeFolds(origin, { seed: live.current.seed });
        const index = Math.min(merged.index(handle.index), merged.corridor.left.length - 2);
        const inserted = insertPair(merged.corridor, index);
        if (!inserted) {
          notify({ text: t("Un fuseau garde au plus 64 sommets par limite"), tone: "warn" });
          return null;
        }
        start = inserted.corridor;
        held = { kind: "vertex", side: handle.side ?? "left", index: inserted.index };
        position = start[held.side][held.index]!;
      } else if (handle.kind === "limaMid") {
        const inserted = insertBend(origin, handle.line, handle.index, at);
        if (!inserted) {
          notify({ text: t("Une LIMA garde au plus 14 courbures"), tone: "warn" });
          return null;
        }
        start = inserted;
        held = { kind: "limaBend", line: handle.line, index: handle.index };
      }
      const kind: SessionKind = held.kind === "vertex" ? "vertex" : held.kind === "limaEnd" ? "limaEnd" : "limaBend";
      const session: GestureSession = {
        kind,
        handle: held,
        line: held.kind === "limaEnd" || held.kind === "limaBend" ? held.line : null,
        origin,
        start,
        frame: frameOf(start),
        grab: grabAt,
        offset: [position[0] - grabAt[0], position[1] - grabAt[1]],
        last: start,
        lastPointer: grabAt,
        blocked: null,
        outside: false,
        anchors: held.kind === "vertex" ? anchorsOf(start, held.side) : null,
        originSelection,
        reference: handle.kind === "vertex" ? start : origin,
        linked: Boolean(options.linked),
      };
      sessionRef.current = session;
      const state = useManoeuvre.getState();
      state.setActive(held);
      state.setHover(null);
      if (held.kind === "vertex" || held.kind === "limaBend") state.select(held);
      if (start !== origin) state.setWorking(start);
      return session;
    },
    [anchorsOf, base, cancelReturn, flushKeys, notify],
  );

  /** Début du glissement d'une LIMA entière dans un fuseau personnalisé (dans ou hors du mode édition). */
  const grabLine = useCallback(
    (line: LineName, grabAt: LonLat): GestureSession | null => {
      cancelReturn();
      flushKeys();
      const origin = base();
      if (!origin || origin.left.length < 2 || !origin.lines[line]) return null;
      const session: GestureSession = {
        kind: "limaWhole",
        handle: null,
        line,
        origin,
        start: origin,
        frame: frameOf(origin),
        grab: grabAt,
        offset: [0, 0],
        last: origin,
        lastPointer: grabAt,
        blocked: null,
        outside: false,
        anchors: null,
        originSelection: null,
        reference: origin,
        linked: false,
      };
      sessionRef.current = session;
      return session;
    },
    [base, cancelReturn, flushKeys],
  );

  /** Butée d'une LIMA sur sa voisine : avis existant (une fois par geste) et étiquette de la voisine qui clignote. */
  const blockedOn = useCallback((session: GestureSession, line: LineName, by: LineName, forward: boolean) => {
    if (session.blocked === by) return;
    session.blocked = by;
    const names = { line: live.current.lineLabel(line), other: live.current.lineLabel(by) };
    live.current.notify({
      text: forward
        ? t("Ordre des lignes : {line} ne peut être déplacée après {other}", names)
        : t("Ordre des lignes : {line} ne peut être déplacée avant {other}", names),
      tone: "warn",
    });
    flashLineTag(by);
  }, []);

  /** Aperçu d'une image du geste : géométrie, validation, puce et trait vers l'autre limite. */
  const drag = useCallback(
    (session: GestureSession, pointer: LonLat, mods: Modifiers) => {
      // Geste abandonné entre-temps (⌘Z, second doigt, toucher annulé) : plus rien à prévisualiser.
      if (sessionRef.current !== session) return;
      session.lastPointer = pointer;
      useGestureState.getState().setPointer(pointer);
      const { bounds: box, level: lvl, zone: free, widthContext: context } = live.current;
      const feedback = useGestureState.getState();
      let next = session.start;
      let tag: Parameters<typeof feedback.setFeedback>[0] = null;
      let tether: [LonLat, LonLat] | null = null;
      let at: LonLat = pointer;
      const wanted: LonLat = [pointer[0] + session.offset[0], pointer[1] + session.offset[1]];
      if (session.kind === "vertex" && session.handle?.kind === "vertex") {
        const { side, index } = session.handle;
        const target = clampToBounds(wanted, box);
        if ((target[0] !== wanted[0] || target[1] !== wanted[1]) && !session.outside) {
          session.outside = true;
          live.current.notify({
            text: free ? t("Hors du terrain") : t("Hors des données du camp"),
            detail: free ? t("Étendez le terrain pour aller plus loin.") : t("L'emprise du camp s'arrête ici."),
            tone: "warn",
            // Zone libre : « Étendre la zone » mène à la fiche de la zone (docs/14 §2.7.8).
            action: free ? extendZoneAction() : undefined,
          });
        }
        const single = !session.linked && (mods.alt || !useManoeuvre.getState().keepWidth);
        if (single) {
          const moved = moveSingle(session.start, side, index, target, {
            lateral: mods.shift,
            bounds: box,
            anchors: session.anchors,
            levelKey: context.levelKey,
          });
          next = moved.corridor;
          const vertex = next[side][index]!;
          const other = closestPoint(next[side === "left" ? "right" : "left"], vertex) ?? vertex;
          tether = [vertex, other];
          at = [(vertex[0] + other[0]) / 2, (vertex[1] + other[1]) / 2];
          tag = widthGestureTag(moved, lvl, context, index, at);
        } else {
          next = moveLinked(session.start, side, index, target, { lateral: mods.shift, bounds: box, anchors: session.anchors });
        }
        at = next[side][index]!;
      } else if (session.kind === "limaEnd" && session.handle?.kind === "limaEnd" && session.line) {
        const result = moveLimaEnd(session.start, session.line, session.handle.side, pointer, session.frame);
        next = result.corridor;
        const line = next.lines[session.line];
        if (result.blockedBy && line) {
          const k0 = session.handle.side === "left" ? 0 : 1;
          const forward = (session.start.lines[result.blockedBy]?.ends[k0] ?? 0) > (session.start.lines[session.line]?.ends[k0] ?? 0);
          blockedOn(session, session.line, result.blockedBy, forward);
        }
        if (line) at = session.frame.point(line.ends[session.handle.side === "left" ? 0 : 1], session.handle.side === "left" ? -1 : 1);
      } else if (session.kind === "limaBend" && session.handle?.kind === "limaBend" && session.line) {
        next = moveBend(session.start, session.line, session.handle.index, wanted, session.frame);
        const bend = next.lines[session.line]?.bends[session.handle.index];
        if (bend) at = session.frame.point(bend[0], bend[1]);
      } else if (session.kind === "limaWhole" && session.line) {
        const result = translateLima(session.start, session.line, session.grab, pointer, session.frame);
        next = result.corridor;
        if (result.blockedBy) {
          const current = session.start.lines[session.line];
          const other = session.start.lines[result.blockedBy];
          const forward = Boolean(current && other && other.ends[0] + other.ends[1] > current.ends[0] + current.ends[1]);
          blockedOn(session, session.line, result.blockedBy, forward);
        }
      }
      session.last = next;
      useManoeuvre.getState().setWorking(next);
      if (session.kind !== "limaWhole") {
        const check = validate(next, box, lvl, context);
        if (check.rules.length) tag = { kind: "rule", at, rule: ruleTag(check.rules[0]!, free, lvl) };
      }
      feedback.setFeedback(tag, tether);
    },
    [blockedOn],
  );

  /** Relâcher : rien d'enregistré en deçà de 10 m ; état invalide : retour animé ; sinon enregistrement. */
  const release = useCallback(
    (session: GestureSession) => {
      if (sessionRef.current !== session) return; // déjà abandonné
      sessionRef.current = null;
      useManoeuvre.getState().setActive(null);
      useGestureState.getState().setFeedback(null);
      const next = session.last;
      const shifted = session.start.left.length !== session.origin.left.length;
      if (!movedEnough(session.reference, next)) {
        restore(session.origin);
        reselect(session.originSelection, shifted);
        return;
      }
      if (session.kind === "limaWhole" && session.line) {
        const check = validate(next, live.current.bounds, live.current.level, live.current.widthContext);
        if (check.rules.length) {
          live.current.notify(refusalNotice(check.rules[0]!, live.current.level, live.current.levelKey));
          animateBack(next, session.origin);
          return;
        }
        // Le serveur glisse la LIMA jusqu'à l'abscisse du point donné (`translate_line`, repère K2) : le milieu de
        // la LIMA glissée, sur l'axe médian.
        const line = next.lines[session.line]!;
        const [lon, lat] = session.frame.point((line.ends[0] + line.ends[1]) / 2, 0);
        useManoeuvre.getState().setWorking(next);
        moveLine.mutate(
          { line: session.line, lon, lat },
          {
            onSettled: () => {
              if (useManoeuvre.getState().working === next) useManoeuvre.getState().setWorking(null);
            },
          },
        );
        return;
      }
      const label =
        session.kind === "limaEnd" || session.kind === "limaBend"
          ? t("retouche de {line}", { line: live.current.lineLabel(session.line ?? "") })
          : undefined;
      if (!commit(next, session.origin, label, session.start)) reselect(session.originSelection, shifted);
    },
    [animateBack, commit, moveLine, reselect, restore],
  );

  /** Geste abandonné (second doigt, sortie du mode) : la géométrie revient sans requête. */
  const abort = useCallback(
    (session: GestureSession | null) => {
      if (!session || sessionRef.current !== session) return;
      sessionRef.current = null;
      useManoeuvre.getState().setActive(null);
      useGestureState.getState().setFeedback(null);
      animateBack(session.last, session.origin, session.start);
      reselect(session.originSelection, session.start.left.length !== session.origin.left.length);
    },
    [animateBack, reselect],
  );

  // --- Clic, double-clic, retrait -----------------------------------------------------------------------------

  const select = useCallback((handle: ManoeuvreHandle | null) => {
    const state = useManoeuvre.getState();
    if (!handle) {
      state.select(null);
      state.selectLine(null);
      return;
    }
    if (handle.kind === "vertex" || handle.kind === "limaBend") state.select(handle);
    if (handle.kind === "limaEnd" || handle.kind === "limaBend" || handle.kind === "limaMid") state.selectLine(handle.line);
  }, []);

  /** Retrait de la paire d'un sommet (deux paires au moins) ou d'une courbure. */
  const removeHandle = useCallback(
    (handle: ManoeuvreHandle | null) => {
      if (!handle) return;
      if (!live.current.canEdit) {
        refuseNotSaved();
        return;
      }
      flushKeys();
      const current = base();
      if (!current) return;
      if (handle.kind === "vertex") {
        const next = removePair(current, handle.index);
        if (!next || next.left.length < 2) {
          notify({ text: t("Un fuseau garde au moins deux sommets par limite"), tone: "warn" });
          return;
        }
        useManoeuvre.getState().select(null);
        commit(next, current, t("retrait d'un sommet"));
      } else if (handle.kind === "limaBend") {
        const next = removeBend(current, handle.line, handle.index);
        if (next === current) return;
        useManoeuvre.getState().select(null);
        commit(next, current, t("retouche de {line}", { line: live.current.lineLabel(handle.line) }));
      }
    },
    [base, commit, flushKeys, notify, refuseNotSaved],
  );

  const removeSelected = useCallback(() => removeHandle(useManoeuvre.getState().selected), [removeHandle]);

  /** « Terminé » ou Échap : la retouche au clavier en attente part, la réponse en cours est attendue (l'aperçu
   *  ne retombe pas sur l'ancien fuseau), puis le mode édition se ferme. */
  const finish = useCallback(async () => {
    flushKeys();
    const pending = inflight.current;
    if (pending) await pending;
    cancelReturn();
    useGestureState.getState().setFeedback(null);
    useManoeuvre.getState().setEditing(false);
  }, [cancelReturn, flushKeys]);

  // --- Clavier ------------------------------------------------------------------------------------------------

  const centerIfHidden = useCallback(
    (at: LonLat) => {
      const m = map();
      if (!m) return;
      const q = m.project(at);
      const box = m.getContainer();
      const margin = 48;
      if (q.x < margin || q.y < margin || q.x > box.clientWidth - margin || q.y > box.clientHeight - margin)
        m.easeTo({ center: at, duration: canAnimate() ? 400 : 0 });
    },
    [map],
  );

  useEffect(() => {
    if (!enabled) return;
    const insideMap = (target: EventTarget | null) => {
      const frame = frameRef.current;
      return Boolean(frame && target instanceof Node && frame.contains(target));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Alt") setAlt(e.type === "keydown");
      // ⌥ / ⇧ pendant un glissement : l'aperçu suit sans attendre un mouvement du pointeur.
      if ((e.key === "Alt" || e.key === "Shift") && sessionRef.current) {
        drag(sessionRef.current, sessionRef.current.lastPointer, { alt: e.altKey, shift: e.shiftKey });
        return;
      }
      if (e.type !== "keydown" || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
      if (isEditableTarget(e.target) || useUi.getState().draft || topEscapePriority() >= ESCAPE.menu) return;
      const onMap = insideMap(e.target) || e.target === document.body;
      const state = useManoeuvre.getState();
      const current = base();
      if (!current) return;
      if (ARROWS.includes(e.key) && onMap && state.selected?.kind === "vertex") {
        // Captées ici (phase de capture, propagation arrêtée) : ⇧← ne fait pas tourner la carte.
        e.preventDefault();
        e.stopPropagation();
        if (!live.current.canEdit) {
          refuseNotSaved();
          return;
        }
        // Retour animé en cours (groupe refusé) : son arrivée est rétablie, le groupe ne repart pas d'une image.
        cancelReturn();
        // Autre sommet que celui du groupe en attente : le groupe part d'abord (une entrée d'historique chacun).
        const pending = keyGroup.current;
        if (pending && (pending.side !== state.selected.side || pending.index !== state.selected.index)) {
          flushKeys();
          cancelReturn();
        }
        const chosen = useManoeuvre.getState().selected;
        if (chosen?.kind !== "vertex") return;
        let group = keyGroup.current;
        if (!group) {
          const origin = base();
          if (!origin?.[chosen.side][chosen.index]) return;
          // Plis de l'amorce effacés au premier appui (`mergeFolds`), le sommet sélectionné gardé.
          const merged = mergeFolds(origin, { keep: chosen.index, seed: live.current.seed });
          const index = merged.index(chosen.index);
          const vertex = merged.corridor[chosen.side][index]!;
          group = {
            origin,
            start: merged.corridor,
            timer: 0,
            side: chosen.side,
            index,
            originIndex: chosen.index,
            target: vertex,
            anchors: anchorsOf(merged.corridor, chosen.side),
          };
          if (index !== chosen.index) state.select({ kind: "vertex", side: chosen.side, index });
        }
        const { side, index, start } = group;
        const m = map();
        const { heading, distance } = arrowStep(e.key as ArrowKey, e.shiftKey, m?.getBearing() ?? 0);
        // Position voulue cumulée depuis le début du groupe : la géométrie se recalcule depuis son départ.
        group.target = moveM(group.target, heading, distance);
        const box = live.current.bounds;
        const options = { bounds: box, anchors: group.anchors, levelKey: live.current.levelKey };
        const next =
          e.altKey || !state.keepWidth
            ? moveSingle(start, side, index, group.target, { ...options, magnet: false }).corridor
            : moveLinked(start, side, index, group.target, options);
        window.clearTimeout(group.timer);
        group.timer = window.setTimeout(flushKeys, ARROW_GROUP_MS);
        keyGroup.current = group;
        state.setWorking(next);
        // Règle violée : puce qui la nomme au sommet (tirets rouges sur les segments en faute, par le calque) ;
        // le groupe refusé est expliqué par un avis à son envoi.
        const check = validate(next, box, live.current.level, live.current.widthContext);
        const at = next[side][index]!;
        useGestureState
          .getState()
          .setFeedback(
            check.rules.length ? { kind: "rule", at, rule: ruleTag(check.rules[0]!, live.current.zone, live.current.level) } : null,
          );
        centerIfHidden(at);
        return;
      }
      if (e.key === "Tab" && insideMap(e.target)) {
        // Au-delà du dernier sommet (ou avant le premier), Tab reprend son rôle : le focus quitte la carte.
        const order = vertexOrder(current.left.length);
        const at = state.selected?.kind === "vertex" ? order.findIndex((h) => sameHandle(h, state.selected)) : -1;
        if ((at === order.length - 1 && !e.shiftKey) || (at === 0 && e.shiftKey)) {
          state.select(null);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const nextHandle = cycleVertex(state.selected, current.left.length, e.shiftKey);
        if (!nextHandle) return;
        state.select(nextHandle);
        const width = widths(current)[nextHandle.index] ?? NaN;
        useGestureState.getState().announce(vertexAnnouncement(nextHandle, current.left.length, width));
        centerIfHidden(current[nextHandle.side][nextHandle.index]!);
        return;
      }
      if ((e.key === "Backspace" || e.key === "Delete") && onMap && state.selected) {
        e.preventDefault();
        e.stopPropagation();
        removeHandle(state.selected);
      }
    };
    // ⌥ relâché hors de la fenêtre (⌥⌘Tab, perte du focus) : aucun keyup ne viendrait le rendre.
    const releaseAlt = () => setAlt(false);
    const onVisibility = () => {
      if (document.visibilityState !== "visible") setAlt(false);
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("keyup", onKey, true);
    window.addEventListener("blur", releaseAlt);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("keyup", onKey, true);
      window.removeEventListener("blur", releaseAlt);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, frameRef, base, map, anchorsOf, drag, flushKeys, cancelReturn, centerIfHidden, removeHandle, refuseNotSaved]);

  // Échap (pile partagée) : désélection, puis sortie du mode édition ; un tracé en cours garde la main.
  useEffect(() => {
    if (!enabled) return;
    const offSelection = registerEscape(ESCAPE.selection, () => {
      if (useUi.getState().draft) return false;
      const state = useManoeuvre.getState();
      if (!state.selected && !state.selectedLine) return false;
      state.select(null);
      state.selectLine(null);
      return true;
    });
    const offEditing = registerEscape(ESCAPE.editing, () => {
      if (useUi.getState().draft || sessionRef.current) return false;
      void finish();
      return true;
    });
    return () => {
      offSelection();
      offEditing();
    };
  }, [enabled, finish]);

  // Sortie du mode édition (« Terminé », panneau, changement de dossier) : geste et retouches en attente.
  useEffect(() => {
    if (enabled) {
      useGestureState.getState().setPointer(null);
      return;
    }
    const group = keyGroup.current;
    if (group) {
      window.clearTimeout(group.timer);
      keyGroup.current = null;
    }
    sessionRef.current = null;
    cancelReturn();
    useGestureState.getState().setFeedback(null);
    setAlt(false);
  }, [enabled, cancelReturn]);

  // Garde des annulations (⌘Z, ↶ ↷) : glissement en cours abandonné, flèches en attente défaites sans requête
  // (⇧⌘Z les enregistre), retouche en vol attendue avant d'annuler (sinon l'annulation viserait l'action d'avant,
  // puis la retouche l'écraserait). Posée dans et hors du mode édition (LIMA entière d'un fuseau personnalisé).
  useEffect(() => {
    const guard = (step: HistoryStep): boolean => {
      const session = sessionRef.current;
      if (session) {
        abort(session);
        return true;
      }
      const group = keyGroup.current;
      if (group) {
        if (step === "redo") {
          flushKeys();
          return true;
        }
        window.clearTimeout(group.timer);
        keyGroup.current = null;
        useGestureState.getState().setFeedback(null);
        const shown = useManoeuvre.getState().working;
        if (shown) animateBack(shown, group.origin, group.start);
        else restore(group.origin);
        reselect(
          { kind: "vertex", side: group.side, index: group.originIndex },
          group.start.left.length !== group.origin.left.length,
        );
        return true;
      }
      const pending = inflight.current;
      if (pending) {
        if (step === "undo") void pending.then((saved) => saved && history.undo());
        return true;
      }
      return false;
    };
    historyGuard = guard;
    // Phase de capture : avant les raccourcis de la carte (useHistoryKeys), qui ignorent un appui consommé.
    const onKey = (e: KeyboardEvent) => {
      const step = historyKeyAction(e);
      if (!step || e.defaultPrevented || !history.isActive() || useUi.getState().draft) return;
      if (topEscapePriority() >= ESCAPE.modal) return;
      if (isEditableTarget(e.target) || isEditableTarget(document.activeElement)) return;
      if ((measureHistoryGuard?.(step) ?? false) || guard(step)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      if (historyGuard === guard) historyGuard = null;
    };
  }, [abort, animateBack, flushKeys, reselect, restore]);

  // Passage à un autre dossier, ou fuseau non éditable (CENTAC) : le mode édition se ferme. La création du
  // dossier (aucun → nouveau, parcours « Enregistrer » désigné du §2.11) le laisse ouvert.
  const previousExercise = useRef(exerciseId);
  useEffect(() => {
    const previous = previousExercise.current;
    previousExercise.current = exerciseId;
    if (previous !== null && previous !== exerciseId) useManoeuvre.getState().setEditing(false);
  }, [exerciseId]);
  useEffect(() => {
    if (editing && deployment && (model === null || deployment.centac)) useManoeuvre.getState().setEditing(false);
  }, [editing, deployment, model]);

  const cursor = useMemo(
    () => (enabled ? handleCursor(active ?? hover, { active: active !== null, alt, canEdit }) : null),
    [enabled, active, hover, alt, canEdit],
  );

  return {
    /** Mode édition ouvert sur un fuseau éditable. */
    enabled,
    canEdit,
    busy: writes.busy,
    selected,
    selectedLine,
    cursor,
    pick,
    hoverAt,
    leave,
    grab,
    grabLine,
    drag,
    release,
    abort,
    touchAt,
    select,
    removeHandle,
    removeSelected,
    refuseNotSaved,
    finish,
  };
}

/** Étiquette de LIMA qui clignote (voisine contre laquelle une LIMA bute, LIMA désignée). */
export function flashLineTag(name: LineName): void {
  if (typeof document === "undefined") return;
  flashAttention(document.querySelector<HTMLElement>(`[data-line-tag="${name}"]`), "field");
}

/** Avis discret après une retouche enregistrée : seulement si le serveur signale quelque chose de nouveau
 *  (avertissement du fuseau absent de la réponse précédente, avis du serveur) — chaque geste ne produit pas son
 *  avis, mais rien de ce que le serveur dit n'est perdu. La mention des ordres complète le détail. */
export function notifyNewWarnings(
  result: Deployment,
  previous: Deployment | null | undefined,
  notify: (notice: Omit<Notice, "id">) => void,
): boolean {
  const before = new Set(previous?.corridor?.warnings ?? []);
  const fresh = (result.corridor?.warnings ?? []).filter((w) => !before.has(w));
  const notes = result.notices ?? [];
  if (!fresh.length && !notes.length) return false;
  const warnings = fresh.map((w) => tServer(w)).join(" · ");
  notify({
    text: t("Fuseau modifié"),
    detail: withOrders(warnings || undefined, result),
    tone: fresh.length ? "warn" : "info",
    ...orderActions({ label: tc("défaire", "Annuler"), run: () => runHistoryStep("undo") }, result),
  });
  return true;
}
